import { CSSObject, Theme } from '@mui/material'
import { Row } from '@tanstack/react-table'
import _ from 'lodash'
import { useContext } from 'react'
import { generatePath, To } from 'react-router-dom'

import { useMergeFormData } from 'components/Form'
import { Table } from 'components/Table'
import { PaginationProps } from 'components/Table/Pagination'
import { useSort } from 'components/Table/Sort'
import {
  AdUnitProductsAttributes,
  AdUnitTableSchema,
} from 'features/inventory/ad-unit/forms/AdUnitTableForm/schema'
import { adUnitToProductsAttributes } from 'features/inventory/ad-unit/pages/AdUnitList/utils'
import { useWorkspaceParam } from 'features/workspace/hooks/useWorkspaceParam'
import { useAbility } from 'providers/casl'
import { useTableSearchParams } from 'providers/router/hooks/useTableSearchParams'
import { useUrlSearchParams } from 'providers/router/hooks/useUrlSearchParams'
import { calculatePagination } from 'providers/tanstack-query/utils'
import { ROUTES } from 'routes'
import { AdUnitListContext } from '../../contexts/AdUnitListContext'
import { DEFAULT_SORT, SORTABLE_COLUMNS } from './constants'
import { useAdUnits } from './hooks/useAdUnits'
import { Alert, useGetAlerts } from './hooks/useGetAlerts'
import { QueryProps } from './types/api'
import { AdUnitForTable, AdUnitSortBy } from './types/model'
import { useColumns } from './useColumns'

export const AdUnitsTable = (): React.JSX.Element => {
  const ability = useAbility()
  const { workspaceId } = useWorkspaceParam()
  const { filters } = useContext(AdUnitListContext)
  const { page, rowsPerPage, setPage, setRowsPerPage } = useTableSearchParams()
  const { urlParams } = useUrlSearchParams()
  const { props: sortProps, sort } = useSort<AdUnitForTable, AdUnitSortBy>(
    DEFAULT_SORT,
    SORTABLE_COLUMNS,
  )

  const queryVariables: QueryProps = {
    filters,
    ...calculatePagination({ page, rowsPerPage }),
    sort,
  }
  const { adUnits, count, loading, setAdUnits } = useAdUnits(queryVariables)
  const alerts: Alert[] = useGetAlerts(queryVariables)

  const columns = useColumns({ alerts, count })

  const adUnitsAttributes: Record<string, AdUnitProductsAttributes> = _.chain(adUnits)
    .keyBy('id')
    .mapValues(adUnitToProductsAttributes)
    .value()

  useMergeFormData<AdUnitTableSchema>({
    data: adUnitsAttributes,
    path: 'adUnitsAttributes',
  })

  const handleRowClick = (row: AdUnitForTable): To => ({
    pathname: generatePath(ROUTES.INVENTORY.AD_UNIT.EDIT, {
      id: row.id,
      tab: 'general',
      workspaceId,
    }),
    search: urlParams,
  })

  const handleStyleRow = (row: Row<AdUnitForTable>, theme: Theme): CSSObject => {
    const {
      original: { ybAdUnitEnabled },
    } = row

    let styles: CSSObject = {}
    if (ybAdUnitEnabled) {
      styles = {
        ...styles,
        backgroundColor: theme.palette.cream.main,
      }
    }

    return styles
  }

  const paginationProps: PaginationProps = {
    count,
    page,
    rowsPerPage,
    setPage,
    setRowsPerPage,
  }

  return (
    <Table<AdUnitForTable>
      columns={columns}
      data={adUnits}
      loading={loading}
      meta={{
        stickyHeader: true,
        styleRow: handleStyleRow,
      }}
      onClickRow={ability.can('access', 'AdUnitPage-edit') ? id => handleRowClick(id) : undefined}
      onUpdateData={setAdUnits}
      pagination={paginationProps}
      sort={sortProps}
    />
  )
}
